import { useRef, useState } from "react";
import { ContextMenu, type ContextMenuGroup } from "@/components/common/ContextMenu";
import { IconButton } from "@/components/common/IconButton";
import {
	CHAT_LIST_FILTER_KIND_LABELS,
	CHAT_STATUS_FILTER_OPTIONS,
	CHAT_VISIBILITY_FILTER_OPTIONS,
	findChatListFilter,
	removeChatListFilter,
	upsertChatListFilter,
	type ChatListFilter,
} from "@/lib/chat-list-filters";

/** status の絞り込みが無いときの表示 (アーカイブ済みを除く)。 */
const ACTIVE_STATUS_OPTION = { value: "active", label: "有効のみ" } as const;

const BUTTON_SIZE = 22;

interface ChatListFilterButtonProps {
	filters: readonly ChatListFilter[];
	onChange: (filters: ChatListFilter[]) => void;
}

/**
 * チャット一覧の絞り込みボタン。押すと種類ごとの選択肢をまとめたポップオーバーを開き、
 * 続けて複数の種類を切り替えられるよう選んでも閉じない。
 */
export function ChatListFilterButton({ filters, onChange }: ChatListFilterButtonProps) {
	const [open, setOpen] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);

	const visibility = findChatListFilter(filters, "visibility")?.value ?? "all";
	const status = findChatListFilter(filters, "status")?.value ?? ACTIVE_STATUS_OPTION.value;
	const visibilityLabel = CHAT_VISIBILITY_FILTER_OPTIONS.find((o) => o.value === visibility)?.label;
	const statusLabel = [ACTIVE_STATUS_OPTION, ...CHAT_STATUS_FILTER_OPTIONS].find(
		(o) => o.value === status,
	)?.label;

	const groups: ContextMenuGroup[] = [
		{
			label: CHAT_LIST_FILTER_KIND_LABELS.visibility,
			items: CHAT_VISIBILITY_FILTER_OPTIONS.map((o) => ({
				key: `visibility-${o.value}`,
				label: o.label,
				selectable: true,
				selected: o.value === visibility,
				closeOnSelect: false,
				testId: `chat-filter-option-visibility-${o.value}`,
				onSelect: () =>
					onChange(upsertChatListFilter(filters, { kind: "visibility", value: o.value })),
			})),
		},
		{
			label: CHAT_LIST_FILTER_KIND_LABELS.status,
			items: [
				{
					key: `status-${ACTIVE_STATUS_OPTION.value}`,
					label: ACTIVE_STATUS_OPTION.label,
					selectable: true,
					selected: status === ACTIVE_STATUS_OPTION.value,
					closeOnSelect: false,
					testId: `chat-filter-option-status-${ACTIVE_STATUS_OPTION.value}`,
					onSelect: () => onChange(removeChatListFilter(filters, "status")),
				},
				...CHAT_STATUS_FILTER_OPTIONS.map((o) => ({
					key: `status-${o.value}`,
					label: o.label,
					selectable: true,
					selected: o.value === status,
					closeOnSelect: false,
					testId: `chat-filter-option-status-${o.value}`,
					onSelect: () =>
						onChange(upsertChatListFilter(filters, { kind: "status", value: o.value })),
				})),
			],
		},
	];

	return (
		<>
			<IconButton
				ref={buttonRef}
				icon="filter"
				iconSize={14}
				size={BUTTON_SIZE}
				color="var(--fg-3)"
				title={`絞り込み: ${visibilityLabel} · ${statusLabel}`}
				data-testid="chat-filter-button"
				style={{ flexShrink: 0 }}
				onClick={() => setOpen((v) => !v)}
			/>
			{open && buttonRef.current && (
				<ContextMenu
					position={{ anchor: buttonRef.current, align: "right" }}
					onClose={() => setOpen(false)}
					testId="chat-filter-popover"
					minWidth={180}
					items={groups}
				/>
			)}
		</>
	);
}
